import React, { useState, useEffect, useRef, memo, useMemo, useCallback } from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import useStore from '../../store/useStore';

const ChartWidget = memo(({ title, data = [], chartType = 'line', dataKey = 'value' }) => {
  const ChartComponent = useMemo(
    () => (chartType === 'area' ? AreaChart : LineChart),
    [chartType]
  );
  const DataComponent = useMemo(
    () => (chartType === 'area' ? Area : Line),
    [chartType]
  );
  const animationsEnabled = useStore((state) => state.animationsEnabled);

  const [displayData, setDisplayData] = useState(data);
  const animationFrameId = useRef(null);
  const displayDataRef = useRef(data);

  // Keep ref in sync with display data
  useEffect(() => {
    displayDataRef.current = displayData;
  }, [displayData]);

  useEffect(() => {
    // Cancel ongoing animation
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
    }

    if (!data || data.length === 0) {
      setDisplayData(data);
      displayDataRef.current = data;
      return;
    }

    // No animation - just set data
    if (!animationsEnabled) {
      setDisplayData(data);
      displayDataRef.current = data;
      return;
    }

    // Same data - skip
    if (JSON.stringify(displayDataRef.current) === JSON.stringify(data)) {
      return;
    }

    // Animate from current display to new data
    const startData = displayDataRef.current.length > 0 ? displayDataRef.current : data;
    const endData = data;
    const duration = 800;
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease in-out
      const eased = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;

      const interpolated = endData.map((endPoint, index) => {
        const startPoint = startData[index] || endPoint;
        const startValue = typeof startPoint[dataKey] === 'number' ? startPoint[dataKey] : 0;
        const endValue = typeof endPoint[dataKey] === 'number' ? endPoint[dataKey] : 0;

        return {
          ...endPoint,
          [dataKey]: startValue + (endValue - startValue) * eased
        };
      });

      setDisplayData(interpolated);

      if (progress < 1) {
        animationFrameId.current = requestAnimationFrame(animate);
      } else {
        animationFrameId.current = null;
      }
    };

    animationFrameId.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [data, dataKey, animationsEnabled]);

  // Memoize formatXAxis function - handle any type of x-axis value
  const formatXAxis = useCallback((value) => {
    // If it's already a formatted string (time, date, number), just return it
    if (typeof value === 'string') {
      // Try to parse as a full ISO date/timestamp
      if (value.includes('T') || value.includes('-') && value.length > 10) {
        try {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            return date.toLocaleTimeString('en-GB', {
              hour: '2-digit',
              minute: '2-digit',
            });
          }
        } catch {
          // Not a valid date, return as-is
        }
      }
      // Return the value as-is (could be "14:30", "2025-01-15", or any label)
      return value;
    }
    // For numbers or other types, convert to string
    return String(value);
  }, []);

  // Memoize tooltip styles
  const tooltipStyle = useMemo(() => ({
    backgroundColor: '#2d2d2d',
    border: '1px solid #404040',
    borderRadius: '4px',
  }), []);

  const labelStyle = useMemo(() => ({ color: '#ffffff' }), []);

  return (
    <div className="widget">
      <div className="widget-header">
        <span className="widget-title">{title}</span>
      </div>
      <div className="widget-body">
        {displayData && displayData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <ChartComponent
              data={displayData}
              margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#404040"
              />
              <XAxis
                dataKey="time"
                tickFormatter={formatXAxis}
                stroke="#b0b0b0"
                style={{ fontSize: '0.75rem' }}
              />
              <YAxis
                stroke="#b0b0b0"
                style={{ fontSize: '0.75rem' }}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                labelStyle={labelStyle}
              />
              <DataComponent
                type="monotone"
                dataKey={dataKey}
                stroke="#4a9eff"
                fill="#4a9eff"
                fillOpacity={chartType === 'area' ? 0.3 : 1}
                strokeWidth={2}
                isAnimationActive={false}
              />
            </ChartComponent>
          </ResponsiveContainer>
        ) : (
          <div style={{ textAlign: 'center', color: '#b0b0b0' }}>No data available</div>
        )}
      </div>
    </div>
  );
});

ChartWidget.displayName = 'ChartWidget';

export default ChartWidget;
